// THE GEOMETRY OF THE BRANCH SOMEBODY JUST OPENED, fetched because they
// opened it.
//
// A streaming export builds a fragment when it is worth its pixels, so a tree
// row's mesh icon (viewer.checkMesh -> meshesObject.has(uid)) says "not built"
// for most rows most of the time - and reads as "this model is missing". The
// worst case is the one the owner meets: click an element in 3D, the tree
// opens to its row, and the five neighbouring rows all wear the icon.
//
// This hook watches which branches are open, asks the server which tiles hold
// them (bounded - see helpers/lazyCategoryGeometry for the measurements that
// set the bound), hands those tiles to the streaming controller, and keeps the
// rows re-rendering while the geometry arrives.
//
// WHAT IT DELIBERATELY DOES NOT DO:
//   - it does not ask at category level unless a category is the deepest thing
//     open. One category spans up to 692 tiles on the biggest staging commit;
//     one type group spans 3 at the median.
//   - it does not promise geometry forever. If a tile has not produced the
//     element's mesh within the window below, the row goes back to saying
//     exactly what it said before this feature existed. A row that keeps
//     claiming "on its way" for a minute is a worse lie than the one we set
//     out to fix.
import { useEffect, useRef, useState } from "react";
import { askForBranchGeometry, branchesToAsk, ROW_BUDGET } from "helpers/lazyCategoryGeometry";
import { extractLeafUids } from "./useStreamingTreeNode";
import { rowIsIncomplete } from "helpers/treeRowUids";

// How often the rows are re-checked while geometry is on its way. The check is
// a Set lookup per pending uid; the re-render is the point of it.
const POLL_MS = 700;
// How long a row may say "on its way" before it goes back to saying nothing.
const WINDOW_MS = 30000;

export default function useBranchGeometry({
  projectId, commit, sections, openNodes, WebGL, enabled = true,
  // THE PAGE'S OWN ANSWER TO "what elements does this row stand for"
  // (helpers/treeRowUids.resolveRowUids, which the eye and isolate already
  // use). On an index-driven tree every row below the one you opened is still
  // a "Loading..." placeholder, so walking the subtree gives nothing - which
  // is why three versions of this hook asked for nothing on the real page.
  // Optional: without it only rows whose leaves are already present are asked
  // about, which is the right behaviour for a tree that has no such resolver.
  resolveRowUids,
}) {
  const [asked, setAsked] = useState(null);       // Set of uids asked for
  const [untiled, setUntiled] = useState(null);   // Set of uids this export never placed
  const [meshVersion, setMeshVersion] = useState(0);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const askedKeys = useRef(new Set());
  const pending = useRef(new Map());              // uid -> the moment it stops being "on its way"

  // --- ask for whatever is newly open ------------------------------------
  useEffect(() => {
    if (!enabled || !projectId) return;
    const viewer = WebGL && WebGL.current && WebGL.current.viewer;
    if (!viewer) return;
    // A BRANCH IS A CANDIDATE WHEN IT HAS ROWS ANYWHERE BENEATH IT, which means
    // walking to its leaves rather than glancing at its children. An
    // index-driven branch opens with a "Loading..." placeholder and gets its
    // real rows a moment later (helpers/recordIndexTree), so a shallow test
    // sees nothing at the moment of opening.
    // A branch is worth asking about when it has rows beneath it OR when it
    // stands for rows that have not been listed yet (an index row). The second
    // case is the common one and the one the earlier versions missed.
    const worth = (n) => extractLeafUids(n).length > 0 || (!!resolveRowUids && rowIsIncomplete(n));
    const todo = branchesToAsk(sections, openNodes, askedKeys.current, worth);
    // WHY NOTHING WAS ASKED FOR, said out loud to /client-log.
    //
    // Three versions of this hook asked for nothing on the real page and the
    // only evidence was an absence: no network call, no error, nothing to
    // read. An instrument that reports only success cannot tell you which of
    // five reasons you are looking at. Throttled hard and best effort; it can
    // never throw into a render.
    try {
      if (typeof window !== "undefined" && window.__branchGeomTrace !== false) {
        const open = (openNodes instanceof Set ? [...openNodes] : (openNodes || []));
        const counted = { open: open.length, todo: todo.length, asked: askedKeys.current.size };
        if (!todo.length && open.length) {
          const all = branchesToAsk(sections, openNodes, askedKeys.current, () => true);
          counted.candidatesIgnoringRows = all.length;
          counted.sample = all.slice(0, 3).map((b) => ({
            key: String(b.key).slice(-42),
            leaves: extractLeafUids(b.node).length,
            incomplete: rowIsIncomplete(b.node),
            kids: (b.node && b.node.nodes && b.node.nodes.length) || 0,
            placeholderKids: ((b.node && b.node.nodes) || []).filter((k) => k && k.placeholder).length,
            lazyKids: ((b.node && b.node.nodes) || []).filter((k) => k && k.lazy).length,
          }));
        }
        window.__branchGeom = counted;
      }
    } catch (e) { /* an instrument must never be the fault */ }
    if (!todo.length) return undefined;
    // THE BACKLOG MUST SURVIVE THE NEXT RENDER.
    //
    // This cancelled its own loop on every effect re-run (live = false in the
    // cleanup), and opening a branch changes the tree and the open set at
    // once - so the in-flight loop was cancelled before it reached the second
    // or third branch, every time. The instrument is what showed it: `asked`
    // climbed 4, 5, 7, 8 while `todo` climbed 2, 3, 5, 6, 7, 8 - a backlog
    // that grew because each run cancelled the last.
    //
    // Only an unmount may stop it now. Duplicates are already impossible:
    // askedKeys is checked before each ask and written as soon as one is made.
    alive.current = true;
    (async () => {
      for (const branch of todo) {
        // An index-driven section row stands for every element under it - tens
        // of thousands on BA03 - so only the rows the tree is showing are asked
        // about (ROW_BUDGET in helpers/lazyCategoryGeometry). The rest arrive as
        // the view reaches them, exactly as they do today.
        let all = extractLeafUids(branch.node);
        if (!all.length && resolveRowUids) {
          // eslint-disable-next-line no-await-in-loop
          const got = await resolveRowUids(branch.node).catch(() => null);
          if (!alive.current) return;
          // TOO MANY TO ACT ON FROM THE TREE (SpaceX Electrical Fixtures is
          // over a million): said, never half done - the same rule the eye and
          // isolate already follow for this row.
          if (got && got.tooMany) {
            askedKeys.current.add(branch.key);
            setNote(`This row holds ${got.count || "too many"} elements to fetch from the tree - open a type under it, or move the view to it.`);
            continue;
          }
          all = (got && got.uids) || [];
        }
        const uids = all.slice(0, ROW_BUDGET);
        // ONLY A BRANCH WE ACTUALLY ASKED ABOUT COUNTS AS ASKED. This marked
        // the key before knowing, so a branch opened while its rows were still
        // the "Loading..." placeholder was recorded as done and never asked
        // about again once its rows arrived - which is most branches, because
        // that is how opening one works. Found by driving the page: every
        // branch opened, no /model-tree call ever made.
        if (!uids.length && !branch.scope) continue;
        askedKeys.current.add(branch.key);
        // eslint-disable-next-line no-await-in-loop
        const out = await askForBranchGeometry({ projectId, commit, scope: branch.scope, uids, viewer });
        if (!alive.current) return;
        if (out.note) setNote(out.note);
        if (!out.asked) continue;
        const until = Date.now() + WINDOW_MS;
        // THE ROWS THE EXPORT NEVER PLACED, BY NAME. Asked about particular
        // rows, the server names them (untiledUids); asked about a scope it can
        // only count them, and then the only honest thing is to say nothing
        // about any single row. Either way these never go in the pending set:
        // a row that waits thirty seconds for geometry that cannot arrive is
        // the same lie as the icon this feature set out to fix.
        const named = (out.untiledUids || []).map(String);
        if (named.length) {
          setUntiled((prev) => { const next = new Set(prev || []); for (const u of named) next.add(u); return next; });
        } else if (out.elementCount > 0 && out.untiled === out.elementCount && uids.length) {
          setUntiled((prev) => { const next = new Set(prev || []); for (const u of uids) next.add(u); return next; });
          continue;
        }
        const gone = new Set(named);
        const waiting = uids.filter((u) => !gone.has(String(u)));
        if (!waiting.length) continue;
        for (const u of waiting) if (!pending.current.has(u)) pending.current.set(u, until);
        setAsked((prev) => { const next = new Set(prev || []); for (const u of waiting) next.add(u); return next; });
      }
    })();
    // No cleanup that stops the loop: see above. The unmount guard is below.
    return undefined;
    // resolveRowUids is deliberately not a dependency: the page rebuilds that
    // arrow on every render, so naming it here would re-run this effect every
    // render. What must re-run it is the tree or the open set changing, and
    // both are named.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, commit, sections, openNodes, WebGL, enabled]);

  // --- keep the rows honest while it arrives ------------------------------
  //
  // There is no residency event to subscribe to: meshes are built inside the
  // streaming applicator, which tells the ViewerStateStore about hide, show
  // and select, not about geometry appearing. So this polls - but only while
  // something is actually pending, and it stops the moment the last row is
  // either built or out of time.
  useEffect(() => {
    if (!pending.current.size) return undefined;
    const viewer = WebGL && WebGL.current && WebGL.current.viewer;
    if (!viewer || typeof viewer.checkMesh !== "function") return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      const done = [];
      for (const [uid, until] of pending.current) {
        if (now > until || viewer.checkMesh(uid)) done.push(uid);
      }
      if (done.length) {
        for (const uid of done) pending.current.delete(uid);
        setAsked((prev) => {
          if (!prev) return prev;
          const next = new Set(prev);
          for (const uid of done) next.delete(uid);
          return next.size ? next : null;
        });
      }
      // The version moves on every tick while anything is pending: a row whose
      // mesh arrived has nothing else to tell the memo comparator.
      setMeshVersion((v) => v + 1);
      if (!pending.current.size) clearInterval(id);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [asked, WebGL]);

  return { askedUids: asked, untiledUids: untiled, meshVersion, geometryNote: note };
}
